import { softFail } from "./errors";

export type Resolver<T> = (value: T | PromiseLike<T>) => void;
export type Rejecter = (reason?: any) => void;

type CleanupFn = () => void | Promise<void>;

export type PromiseExecutor<T> = (
  resolve: Resolver<T>,
  reject: Rejecter
) => void | CleanupFn;

export type OnFulfilled<T, TResult = T> = (
  value: T
) => TResult | PromiseLike<TResult>;
export type OnRejected<TResult> = (
  reason: any
) => TResult | PromiseLike<TResult>;

export function promiseDelay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type PromiseHelper<TVal, TArgs extends any[]> = (
  ctrl: PromiseController<TVal>,
  ...args: TArgs
) => void | (() => void);

export class TimeoutError extends Error {
  constructor(message: string | number) {
    super(
      typeof message === "number"
        ? `Operation timed out after ${message}ms`
        : message
    );
    this.name = "TimeoutError";
  }
}

class PromiseCleanupError extends Error {
  constructor(
    message: string,
    public readonly cause: unknown
  ) {
    super(message);
    this.name = "PromiseCleanupError";
  }
}

export function timeoutHelper<T = unknown>(
  ctrl: PromiseController<T>,
  ms: number,
  message: string = `Operation timed out after ${ms}ms`,
  ErrClass: new (message: string) => Error = TimeoutError
): void | (() => void) {
  if (typeof ms !== "number") {
    return;
  }

  const timeoutId = setTimeout(() => ctrl.reject(new ErrClass(message)), ms);
  return () => clearTimeout(timeoutId);
}
export class PromiseController<T> implements PromiseLike<T> {
  private readonly _promise: Promise<T>;
  private readonly _resolve: Resolver<T>;
  private readonly _reject: Rejecter;
  private readonly cleanups: CleanupFn[] = [];
  private _completed = false;
  private _awaited = false;

  public get promise(): Promise<T> {
    this._awaited = true;
    return this._promise;
  }

  public constructor(cb: PromiseExecutor<T>) {
    ({
      promise: this._promise,
      resolve: this._resolve,
      reject: this._reject,
    } = Promise.withResolvers());

    try {
      // route the executor's callbacks through the public resolve/reject so that
      // settling from inside the executor also runs cleanups and marks completion
      const cleaner = cb(
        (value) => this.resolve(value),
        (reason) => this.reject(reason)
      );
      this.cleanup(cleaner);
    } catch (error) {
      this.reject(error);
      throw error;
    }
  }

  private cleanup(fn?: CleanupFn | void): PromiseController<T> {
    if (!fn) {
      return this;
    }
    // if the controller already settled (e.g. the executor resolved
    // synchronously before returning its cleaner), run the cleanup now rather
    // than parking it in a list that will never be drained again
    if (this._completed) {
      this.runCleanup(fn);
    } else {
      this.cleanups.push(fn);
    }
    return this;
  }

  private async runCleanup(fn: CleanupFn): Promise<void> {
    try {
      await fn();
    } catch (error) {
      // ensure cleanup errors don't get swallowed
      // but also don't interfere with the cleanup process
      softFail(PromiseCleanupError, "Error during promise cleanup", error);
    }
  }

  public resolve(value: T | PromiseLike<T>) {
    if (this.complete()) {
      this._resolve(value);
    }
  }

  public reject(reason?: any) {
    if (this.complete()) {
      this._reject(reason);
    }
  }

  private complete(): boolean {
    if (this._completed) {
      return false;
    }

    this._completed = true;
    this.cleanups.forEach((fn) => this.runCleanup(fn));
    return true;
  }

  then<TResult1 = T, TResult2 = never>(
    onfulfilled?: OnFulfilled<T, TResult1> | null | undefined,
    onrejected?: OnRejected<TResult2> | null | undefined
  ): PromiseLike<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }

  with<TArgs extends any[]>(
    helper: PromiseHelper<T, TArgs>,
    ...args: TArgs
  ): PromiseController<T> {
    if (this._awaited) {
      throw new Error(
        "Cannot add helper to an already awaited PromiseController"
      );
    }
    if (this._completed) {
      return this;
    }
    // register the helper's cleanup (if any) so it runs when the controller
    // settles — e.g. timeoutHelper clearing its timer, signalHelper removing
    // its abort listener
    return this.cleanup(helper(this, ...args));
  }
}

export function promiseEvent<T extends EventTarget, Ret = T>(
  target: T,
  eventName: string,
  callback: (target: T, event: Event, resolve: Resolver<Ret>) => void
): Promise<Ret> {
  return new PromiseController<Ret>((resolve, reject) => {
    const handler = async (event: Event) => {
      try {
        await callback(target, event, resolve);
      } catch (error) {
        reject(error);
      }
    };
    target.addEventListener(eventName, handler);
    return () => target.removeEventListener(eventName, handler);
  }).promise;
}
